/**
 * Curated web source collector (Layer 1).
 *
 * Fetches content from desk-config-curated web sources (news sites, specialist
 * publications, registries). These are analyst-vetted sources with NATO
 * reliability A-B.
 *
 * Content extraction is basic (strip tags) until Defuddle or Readability
 * is evaluated and integrated.
 */

import type { CollectionTask } from '../types';
import type { WebSource } from '@/config/desk-config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CuratedWebResult {
  rawText: string;
  sourceCount: number;
  collectorId: string;
  error?: string;
}

// ---------------------------------------------------------------------------
// Collection
// ---------------------------------------------------------------------------

const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Collect content from desk-curated web sources.
 *
 * Each source is fetched independently — failures are skipped, not fatal.
 * Results are concatenated with source attribution headers.
 */
export async function collectFromCuratedWeb(
  _task: CollectionTask,
  webSources: WebSource[],
): Promise<CuratedWebResult> {
  const results: string[] = [];

  for (const source of webSources) {
    try {
      const response = await fetch(source.url, {
        signal: AbortSignal.timeout(DEFAULT_TIMEOUT_MS),
        headers: { 'User-Agent': 'Sentinel-OSINT/1.0' },
      });

      if (!response.ok) continue;

      const html = await response.text();
      const text = extractReadableText(html, source.scrapePattern);
      if (text && text.length > 100) {
        results.push(`[Source: ${source.name} (Reliability ${source.reliability})]\n${text}`);
      }
    } catch {
      // Skip failed sources — don't fail the entire task
    }
  }

  return {
    rawText: results.join('\n\n---\n\n'),
    sourceCount: results.length,
    collectorId: 'curated_web',
  };
}

// ---------------------------------------------------------------------------
// Content extraction (basic — to be replaced with Defuddle after evaluation)
// ---------------------------------------------------------------------------

/**
 * Extract readable text from HTML by stripping scripts, styles, and tags.
 *
 * @param html - Raw HTML string
 * @param _selector - CSS selector for content region (unused until DOM parser available)
 */
function extractReadableText(html: string, _selector?: string): string {
  return html
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '')
    .replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, '')
    .replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#039;/gi, "'")
    .replace(/&[a-z]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
